import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { FC, Fragment, useMemo, useState } from "react"
import { runStoresSnapshots } from "shared/reservationSyncData"
import {
  ReservationSyncDto,
  SourceKeyDto,
} from "src/features/things/reservationSyncApi"
import { DriftClassFilters, type ClassFilter } from "./DriftClassFilters"
import { fmtCount, PartLoader } from "./ReservationSyncChrome"
import { SnapshotPairView } from "./SnapshotPairView"
import { SourceStrip } from "./SourceCards"
import { isNonDriftKey } from "./driftClasses"
import { useReservationSyncReport, useSnapshotPair } from "./useReservationSyncReport"

type DriftRowDto = {
  sstId: string
  driftClass: string
  sl2ReservationId?: string
  presence?: Record<SourceKeyDto, boolean>
  nonDriftFlags?: string[]
  diffs?: { field: string; sst?: string; sl2?: string }[]
  /** Fields the partner holds blank while DynamoDB holds a value — never drift (LOCKER-437). */
  partnerBlank?: string[]
  olapDiffs?: { column: string; ddb?: string; olap?: string }[]
}

/**
 * Severity per class, and every class needs an entry.
 *
 * A class that falls through to `default` renders the same undifferentiated grey
 * as one that is deliberately NOT drift — so the two populations an operator most
 * needs to tell apart, "counted into driftTotal" and "lifted out of it on
 * purpose", become indistinguishable in the one place they sit side by side.
 */
const CLASS_COLOR: Record<string, "success" | "primary" | "error" | "warning" | "default"> =
  {
    identical: "success",
    statusMismatch: "primary",
    fieldMismatch: "primary",
    olapMissing: "error",
    // A real projection divergence, counted into driftTotal — same weight as
    // olapMissing, which is the same defect with the row absent rather than stale.
    olapStale: "error",
    onlyInSst: "error",
    onlyInSl2: "warning",
    onlyInOlap: "warning",
    duplicateInSl2: "warning",
    // Never drift, and the one class this ticket went out of its way to lift out
    // of the drift queue. `default` is right: it is the absence of severity.
    orphanReservation: "default",
  }

const SOURCE_COLUMNS: SourceKeyDto[] = ["v1", "v2", "ddb", "olap"]

/**
 * The per-row drift report (LOCKER-421 `DoD8`).
 *
 * Two changes against LOCKER-374's version, and both are about not flattening
 * different kinds of thing into one:
 *
 *  - four PRESENCE columns per row, so a reader can see which sources hold the
 *    reservation before reading what disagrees about it;
 *  - the class filters split into two groups — counted in the drift total, and
 *    counted separately — with the second dashed, because a flat row of chips
 *    gives a defect class and a divergence the same weight.
 *
 * A row's `driftClass` is the precedence winner; its `nonDriftFlags` say what else
 * is true about it. That is why filtering by a counted-never-drift class matches
 * on the FLAG rather than on the class: a `statusMismatch` row with a poisoned
 * expiry belongs in both lists, and hiding it from the second would understate a
 * defect that no sync can close.
 */
export const DriftPanel: FC<{ run: ReservationSyncDto }> = ({ run }) => {
  const { reportOf, loadNextPart } = useReservationSyncReport<DriftRowDto>(run)
  const { snapshotState, loadSnapshot } = useSnapshotPair(run)
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [filter, setFilter] = useState<ClassFilter>({ group: "all" })
  const report = reportOf("drift")

  const rows = useMemo(() => {
    if (filter.group === "all") return report.rows
    if (filter.group === "nonDrift")
      return report.rows.filter((r) => r.nonDriftFlags?.includes(filter.key))
    return report.rows.filter((r) => r.driftClass === filter.key)
  }, [report.rows, filter])

  // Asked once for the whole run, not once per row: a run stored with
  // --no-snapshots has nothing to open on ANY row, and offering the button anyway
  // makes the operator click to learn that.
  const hasSnapshots = runStoresSnapshots(run.objects)

  if (run.driftTotal === null)
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        No drift rows are shown for an inconclusive run. The classification was
        never computed — the sources that answered are still stored, and the
        Objects tab lists them.
      </Typography>
    )

  return (
    <Box data-ui="UI-SL2W-163">
      <Box sx={{ mb: 2 }}>
        <SourceStrip run={run} />
      </Box>

      <PartLoader
        report={report}
        onLoadMore={() => loadNextPart("drift")}
        label="Drift report"
      />
      {!report.partsLoaded && !report.loading && (
        <Button size="small" variant="outlined" onClick={() => loadNextPart("drift")}>
          Load the drift report
        </Button>
      )}

      {report.partsLoaded > 0 && (
        <>
          <DriftClassFilters
            counts={run.counts}
            nonDriftCounts={run.nonDriftCounts}
            driftTotal={run.driftTotal}
            selected={filter}
            onSelect={setFilter}
          />

          {!hasSnapshots && (
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 1, color: "text.secondary" }}
            >
              This run stores no snapshot objects — it was published with
              --no-snapshots, or they have aged out on the bucket lifecycle rule.
              The drift rows below are complete; only the raw pair behind each one is
              unavailable.
            </Typography>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>SST id</TableCell>
                {SOURCE_COLUMNS.map((s) => (
                  <TableCell key={s} align="center" sx={{ width: 52 }}>
                    {s.toUpperCase()}
                  </TableCell>
                ))}
                <TableCell>Class</TableCell>
                <TableCell>DynamoDB reservation id</TableCell>
                <TableCell>Difference</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(0, 400).map((r) => {
                const snap = snapshotState[r.sstId]
                return (
                  <Fragment key={r.sstId}>
                    <TableRow hover>
                      <TableCell sx={{ fontFamily: "monospace" }}>{r.sstId}</TableCell>
                      {SOURCE_COLUMNS.map((s) => {
                        const present = r.presence?.[s]
                        return (
                          <TableCell
                            key={s}
                            align="center"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 600,
                              color: present
                                ? "success.main"
                                : r.driftClass === "identical"
                                  ? "text.disabled"
                                  : "error.main",
                            }}
                          >
                            {present === undefined
                              ? "·"
                              : present
                                ? "✓"
                                : r.driftClass === "identical"
                                  ? "·"
                                  : "×"}
                          </TableCell>
                        )
                      })}
                      <TableCell>
                        <Chip
                          size="small"
                          label={r.driftClass}
                          color={CLASS_COLOR[r.driftClass] ?? "default"}
                          variant="outlined"
                        />
                        {/* The flags, dashed, so a writable row that ALSO carries a
                            never-written defect reads as both rather than as one. */}
                        {(r.nonDriftFlags ?? [])
                          .filter(isNonDriftKey)
                          .map((flag) => (
                            <Chip
                              key={flag}
                              size="small"
                              label={flag}
                              color="warning"
                              variant="outlined"
                              sx={{
                                ml: 0.5,
                                mt: 0.5,
                                borderStyle: "dashed",
                                fontSize: "0.62rem",
                                height: 18,
                              }}
                            />
                          ))}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {r.sl2ReservationId ?? "—"}
                      </TableCell>
                      <TableCell>
                        <DifferenceCell row={r} />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          disabled={!hasSnapshots}
                          title={
                            hasSnapshots ? undefined : "this run stores no snapshot objects"
                          }
                          onClick={() => {
                            setOpen((o) => ({ ...o, [r.sstId]: !o[r.sstId] }))
                            void loadSnapshot(r.sstId)
                          }}
                        >
                          {open[r.sstId] ? "Hide" : "Raw snapshot"}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {open[r.sstId] && (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ p: 0 }}>
                          {snap?.loading && (
                            <Typography variant="caption" sx={{ p: 2, display: "block" }}>
                              fetching the snapshot object for this row…
                            </Typography>
                          )}
                          {snap?.error && (
                            <Typography
                              variant="caption"
                              sx={{ p: 2, display: "block", color: "error.main" }}
                            >
                              {snap.error}
                            </Typography>
                          )}
                          {snap?.pair && (
                            <SnapshotPairView
                              pair={snap.pair}
                              fromKey={snap.fromKey}
                              cached={snap.cached}
                              apiVersion={run.sstApiVersion}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                )
              })}
            </TableBody>
          </Table>
          {rows.length > 400 && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              showing the first 400 of {fmtCount(rows.length)} loaded row(s) — load
              more parts or narrow the filter
            </Typography>
          )}
        </>
      )}
    </Box>
  )
}

/**
 * What diverged, partner-side and OLAP-side, in one cell.
 *
 * The two are labelled separately because they are answers to different questions:
 * a partner diff is "our copy is wrong", an OLAP diff is "the projection of our
 * copy is wrong". Collapsing them would make a `knownProjectionDefect` look like a
 * reservation whose data we hold incorrectly.
 */
export const DifferenceCell: FC<{ row: DriftRowDto }> = ({ row }) => {
  if (row.driftClass === "onlyInSst")
    return (
      <Typography variant="caption" sx={{ color: "error.main" }}>
        no row in DynamoDB at all
      </Typography>
    )
  if (row.driftClass === "olapMissing")
    return (
      <Typography variant="caption">
        Present in DynamoDB, absent from OLAP. The stream did not deliver.
      </Typography>
    )

  const partner = row.diffs ?? []
  const partnerBlank = row.partnerBlank ?? []
  const olap = row.olapDiffs ?? []
  if (!partner.length && !partnerBlank.length && !olap.length) return <>—</>

  return (
    <Box sx={{ fontSize: "0.72rem" }}>
      {partner.map((d) => (
        <Box key={d.field}>
          <Box component="span" sx={{ color: "text.disabled" }}>
            {d.field}{" "}
          </Box>
          <Box component="span" sx={{ fontFamily: "monospace" }}>
            {d.sst ?? "absent"}
          </Box>
          <Box component="span" sx={{ color: "text.disabled" }}>
            {" vs "}
          </Box>
          <Box component="span" sx={{ fontFamily: "monospace" }}>
            {d.sl2 ?? "absent in DynamoDB"}
          </Box>
        </Box>
      ))}
      {partnerBlank.length > 0 && (
        <Box sx={{ color: "text.secondary" }}>
          partner blank: {partnerBlank.join(", ")} — DynamoDB keeps its value
        </Box>
      )}
      {olap.slice(0, 4).map((d) => (
        <Box key={d.column}>
          <Box component="span" sx={{ color: "text.disabled" }}>
            olap.{d.column}{" "}
          </Box>
          <Box component="span" sx={{ fontFamily: "monospace" }}>
            {d.olap ?? "absent"}
          </Box>
          <Box component="span" sx={{ color: "text.disabled" }}>
            {" vs DynamoDB "}
          </Box>
          <Box component="span" sx={{ fontFamily: "monospace" }}>
            {d.ddb ?? "absent"}
          </Box>
        </Box>
      ))}
      {olap.length > 4 && (
        <Box component="span" sx={{ color: "text.disabled" }}>
          + {olap.length - 4} more column(s)
        </Box>
      )}
    </Box>
  )
}
